import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Package, 
  Clock, 
  Users, 
  CheckCircle,
  ArrowRight,
  Warehouse,
  BarChart3,
  ShieldCheck,
  Headphones,
  Zap
} from "lucide-react";

const FulfillmentDienstleisterPage = () => {
  const benefits = [
    {
      icon: Package,
      title: "Ab 20 Sendungen/Monat",
      description: "Kein Mindestvolumen nötig. Wir wachsen mit Ihrem Business – vom Startup bis zum etablierten Online-Händler."
    },
    {
      icon: Clock,
      title: "Same-Day Versand bis 12 Uhr",
      description: "Bestellungen bis 12 Uhr werden noch am selben Tag versendet. Ihre Kunden erhalten ihre Pakete schneller."
    },
    {
      icon: Users,
      title: "Persönlicher Ansprechpartner",
      description: "Kein anonymes Callcenter. Sie haben einen festen Ansprechpartner, der Ihr Business kennt."
    },
    {
      icon: Warehouse,
      title: "500m² Lagerfläche",
      description: "Moderne Lagerhaltung mit Echtzeit-Bestandsübersicht und professionellem Warenmanagement."
    },
    {
      icon: BarChart3,
      title: "Shop-Integration",
      description: "Nahtlose Anbindung an Shopify, WooCommerce, Amazon, eBay und weitere Plattformen."
    },
    {
      icon: ShieldCheck,
      title: "Alles aus einer Hand",
      description: "Lagerung, Kommissionierung, Verpackung und Versand – wir kümmern uns um alles."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Kostenlose Beratung",
      description: "Wir analysieren Ihre Anforderungen und erstellen ein individuelles Angebot."
    },
    {
      step: "02", 
      title: "Onboarding",
      description: "Wir richten Ihre Shop-Anbindung ein und übernehmen Ihren Warenbestand."
    },
    {
      step: "03",
      title: "Fulfillment läuft",
      description: "Bestellungen werden automatisch verarbeitet und noch am selben Tag versendet."
    }
  ];

  const stats = [
    { number: "20+", label: "Jahre Erfahrung" },
    { number: "500+", label: "Zufriedene Kunden" },
    { number: "20k+", label: "Pakete pro Jahr" },
    { number: "180", label: "Länder weltweit" }
  ];

  return (
    <div data-testid="fulfillment-dienstleister-page">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
              Fulfillment-Dienstleister
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Fulfillment ab <span className="text-orange-500">20 Sendungen</span> pro Monat
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl">
              Konzentrieren Sie sich auf Ihr Kerngeschäft – wir übernehmen Lagerung, 
              Kommissionierung und Versand. Mit persönlichem Ansprechpartner und Same-Day Versand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/angebot">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-10 py-6 text-lg">
                  Kostenloses Angebot anfordern
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 uppercase tracking-wider font-bold px-10 py-6 text-lg bg-transparent">
                  Beratungsgespräch vereinbaren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* USP Bar */}
      <section className="bg-orange-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">Ab 20 Sendungen/Monat</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">Same-Day bis 12 Uhr</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">Persönlicher Ansprechpartner</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">180 Länder weltweit</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Ihre Vorteile
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Warum Kauz Logistik als Fulfillment-Partner?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={benefit.title}
                  className="p-8 border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all"
                  data-testid={`benefit-${index}`}
                >
                  <div className="w-14 h-14 bg-orange-100 flex items-center justify-center mb-6">
                    <Icon className="text-orange-600 w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-orange-600 mb-2">{stat.number}</div>
                <div className="text-slate-600 uppercase tracking-wider text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              So einfach geht's
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              In 3 Schritten zum Fulfillment
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={item.step} className="text-center">
                <div className="w-20 h-20 bg-orange-600 text-white text-2xl font-bold rounded-full flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-bold text-xl mb-3 text-slate-900">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
                {index < process.length - 1 && (
                  <ArrowRight className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 text-slate-300" size={32} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                Für wen ist unser Fulfillment?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-8 text-slate-900">
                Ideal für Online-Händler, Startups & KMU
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Zap className="text-white w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-slate-900">E-Commerce & Online-Shops</h4>
                    <p className="text-slate-600">Shopify, WooCommerce, Amazon, eBay – wir integrieren alle gängigen Plattformen.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Users className="text-white w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-slate-900">Startups & Wachstumsunternehmen</h4>
                    <p className="text-slate-600">Ab 20 Sendungen/Monat – wir wachsen mit Ihrem Business.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Headphones className="text-white w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-slate-900">B2B & B2C Unternehmen</h4>
                    <p className="text-slate-600">Endkundenversand oder Händlerbelieferung – wir passen uns an.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/egvlrw92_Ecommerce.jpg"
                alt="E-Commerce Fulfillment"
                className="w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit für professionelles Fulfillment?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Fordern Sie jetzt Ihr kostenloses und unverbindliches Angebot an. 
            Wir melden uns innerhalb von 24 Stunden bei Ihnen.
          </p>
          <Link to="/angebot">
            <Button className="bg-white text-orange-600 hover:bg-slate-100 uppercase tracking-wider font-bold px-12 py-6 text-lg">
              Kostenloses Angebot anfordern
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FulfillmentDienstleisterPage;
